import React, { Fragment, useMemo } from 'react';

import UFOSegment from '@atlaskit/react-ufo/segment';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { NavEmptyState } from '@atlassian/navigation-common/ui/nav-empty-state';
import { NavErrorBoundary } from '@atlassian/navigation-error-boundary';

import { withSideNavigationAnalyticsContext } from '../common/analytics/context';
import type { GlobalSideNavigationProps } from '../common/types';
import { AsyncIntlProvider } from '../common/utils/locale';
import { ItemsAndShortcutsProvider } from '../controllers/items-and-shortcuts-provider';
import { OptionsProvider } from '../controllers/options-provider';
import { PeekMenuIdProviders } from '../controllers/peek-menu-id-providers';

import { GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME } from './constants';
import { SideNavigationLayout } from './layout';
import { messages } from './messages';
import { LocalStore } from './side-nav/LocalStore';
import { NetworkMigrationStore } from './side-nav/NetworkMigrationStore';
import { NetworkStore } from './side-nav/NetworkStore';
import { SideNav } from './side-nav/SideNav';

// Wrap the component in Reactact.memo`, which performs a shallow (referential)
// comparison of all props and bails out when they are all equal;
// The default `React.memo` shallow comparator is exactly what we want here
// (the callers already memoise `items`, `shortcuts`, `forYou`,
// `networkStorageParams`, etc.), so no custom comparator is needed.
const MemoizedRawGlobalSideNavigation: React.MemoExoticComponent<
	(props: GlobalSideNavigationProps) => React.JSX.Element
> = React.memo(RawGlobalSideNavigation);

export const GlobalSideNavigation: React.ComponentType<
	GlobalSideNavigationProps<ObjectListFlyoutItem>
> = withSideNavigationAnalyticsContext(MemoizedRawGlobalSideNavigation);

export default MemoizedRawGlobalSideNavigation; // This is only needed to support the Atlaskit docs. See https://hello.atlassian.net/wiki/spaces/navx/pages/5305993008/Platform+Guide#Atlaskit-%3CProps%3E-component-not-rendering

function RawGlobalSideNavigation(props: GlobalSideNavigationProps): React.JSX.Element {
	const {
		app,
		appLabel,
		cloudId,
		customizationStore = 'local',
		defaultMenuVisibility,
		disableMenuCustomization = false,
		forYou,
		growthItemShortcuts,
		intlMode = 'async',
		isAnonymous = false,
		isShortcutsStatic,
		items,
		networkStorageParams,
		peekMenuId,
		shortcuts,
		userId,
		...innerProps
	} = props;
	const IntlWrapper = intlMode === 'inherit' ? Fragment : AsyncIntlProvider;

	// If user is anon, disable menu customization and use local store
	const effectiveDisableMenuCustomization = disableMenuCustomization || isAnonymous;
	const effectiveCustomizationStore = isAnonymous ? 'local' : customizationStore;

	const Store = useMemo(() => {
		switch (effectiveCustomizationStore) {
			case 'local':
				return LocalStore;
			case 'network-migration':
				return NetworkMigrationStore;
			default:
				return NetworkStore;
		}
	}, [effectiveCustomizationStore]);

	return (
		<UFOSegment name={GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME}>
			<IntlWrapper>
				<ModalContextProvider>
					<NavErrorBoundary
						errorFallbackUI={({ header, description }) => (
							<SideNavigationLayout>
								<NavEmptyState header={header} description={description} />
							</SideNavigationLayout>
						)}
						errorMessages={messages}
						// `location` is no longer used for error-message lookup; it is retained
						// purely to tag the nav-boundary context for Sentry/analytics so errors
						// caught at the top of the side nav remain attributable to this surface.
						location="global-side-navigation"
						sentryConfig={{ product: app }}
						ufoInteractionName={GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME}
					>
						<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SIDE_NAVIGATION}>
							<OptionsProvider
								app={app}
								appLabel={appLabel}
								cloudId={cloudId}
								customizationStore={effectiveCustomizationStore}
								defaultMenuVisibility={defaultMenuVisibility}
								disableMenuCustomization={effectiveDisableMenuCustomization}
								hasBottomElement={!!innerProps?.bottomElem}
								intlMode={intlMode}
								isShortcutsStatic={isShortcutsStatic || isAnonymous}
								l1DragAndDropEnabled={!effectiveDisableMenuCustomization}
								networkStorageParams={networkStorageParams}
								userId={userId}
							>
								<ItemsAndShortcutsProvider
									forYou={isAnonymous ? {} : forYou}
									growthItemShortcuts={growthItemShortcuts}
									items={isAnonymous && props.anonymousItems ? props.anonymousItems : items}
									rawShortcuts={shortcuts}
								>
									<PeekMenuIdProviders value={peekMenuId}>
										<Store>
											<SideNav {...innerProps} />
										</Store>
									</PeekMenuIdProviders>
								</ItemsAndShortcutsProvider>
							</OptionsProvider>
						</BFFProvider>
					</NavErrorBoundary>
				</ModalContextProvider>
			</IntlWrapper>
		</UFOSegment>
	);
}
